import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, Shield, Star } from "lucide-react";
import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { categoryHref } from "@/lib/categories";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import AutoScroll from "embla-carousel-auto-scroll";

interface FeaturedItem {
  id: string;
  title: string;
  price_per_day: number;
  category: string;
  city: string;
  images: string[];
}

// Fisher-Yates shuffle for fair random ordering
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Ensure no duplicate items (same id) are ever rendered at once.
const dedupeById = (items: FeaturedItem[]): FeaturedItem[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const DISPLAY_COUNT = 8;

// Mock featured items shown when no real featured listings exist yet.
// These link to /browse so clicks don't hit a broken item page.
const MOCK_FEATURED_ITEMS: FeaturedItem[] = [
  {
    id: "mock-1",
    title: "Canon EOS R5 Mirrorless Camera",
    price_per_day: 85,
    category: "Cameras",
    city: "Los Angeles, CA",
    images: ["https://images.unsplash.com/photo-1606983340126-99ab4feaa64a?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-2",
    title: "DeWalt 20V Cordless Drill Kit",
    price_per_day: 18,
    category: "Tools",
    city: "Austin, TX",
    images: ["https://images.unsplash.com/photo-1504148455328-c376907d081c?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-3",
    title: "DJI Mavic 3 Pro Drone",
    price_per_day: 120,
    category: "Electronics",
    city: "Miami, FL",
    images: ["https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-4",
    title: "4-Person Camping Tent",
    price_per_day: 25,
    category: "Other",
    city: "Denver, CO",
    images: ["https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-5",
    title: "Bose Party Speaker System",
    price_per_day: 45,
    category: "Party Equipment",
    city: "New York, NY",
    images: ["https://images.unsplash.com/photo-1545454675-3531b543be5d?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-6",
    title: "Trek Fuel EX Mountain Bike",
    price_per_day: 55,
    category: "Sports",
    city: "Portland, OR",
    images: ["https://images.unsplash.com/photo-1532298229144-0ec0c57515c7?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-7",
    title: "PlayStation 5 Console Bundle",
    price_per_day: 35,
    category: "Electronics",
    city: "Chicago, IL",
    images: ["https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-8",
    title: "Anker Nebula Portable Projector",
    price_per_day: 28,
    category: "Electronics",
    city: "Seattle, WA",
    images: ["https://images.unsplash.com/photo-1535016120720-40c646be5580?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-9",
    title: "Fender Stratocaster Electric Guitar",
    price_per_day: 40,
    category: "Music",
    city: "Nashville, TN",
    images: ["https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-10",
    title: "Inflatable Stand-Up Paddleboard",
    price_per_day: 50,
    category: "Sports",
    city: "San Diego, CA",
    images: ["https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-11",
    title: "Sun Joe Electric Pressure Washer",
    price_per_day: 22,
    category: "Tools",
    city: "Phoenix, AZ",
    images: ["https://images.unsplash.com/photo-1640653488366-f7771cd7292d?w=600&h=450&fit=crop"],
  },
  {
    id: "mock-12",
    title: "Meta Quest 3 VR Headset",
    price_per_day: 38,
    category: "Electronics",
    city: "Boston, MA",
    images: ["https://images.unsplash.com/photo-1761521538789-bd477946a639?w=600&h=450&fit=crop"],
  },
];

const FeaturedItems = () => {
  const [allItems, setAllItems] = useState<FeaturedItem[]>([]);
  const [displayedItems, setDisplayedItems] = useState<FeaturedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  
  // All hooks must be called before any conditional returns
  const sectionRef = useRef<HTMLElement>(null);
  const autoScrollPlugin = useRef(
    AutoScroll({
      speed: 0.8,
      startDelay: 0,
      stopOnInteraction: false,
      stopOnMouseEnter: false,
      stopOnFocusIn: false,
      playOnInit: true,
    })
  );
  const resumeAutoScrollTimer = useRef<ReturnType<typeof setTimeout>>();
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const contentY = useTransform(scrollYProgress, [0, 1], [0, 22]);
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.55, 0.85], [0, 1, 1, 0]);

  const showNewItems = useCallback(() => {
    if (allItems.length === 0) return;
    const shuffled = shuffleArray(allItems);
    setDisplayedItems(dedupeById(shuffled).slice(0, DISPLAY_COUNT));
  }, [allItems]);

  useEffect(() => {
    fetchFeaturedItems();
  }, []);

  useEffect(() => {
    if (allItems.length > 0) {
      showNewItems();
    }
  }, [allItems]);

  useEffect(() => {
    return () => {
      if (resumeAutoScrollTimer.current) {
        clearTimeout(resumeAutoScrollTimer.current);
      }
    };
  }, []);

  const moveCarousel = useCallback((direction: "previous" | "next") => {
    if (!carouselApi) return;

    autoScrollPlugin.current.stop();

    if (direction === "previous") {
      carouselApi.scrollPrev();
    } else {
      carouselApi.scrollNext();
    }

    if (resumeAutoScrollTimer.current) {
      clearTimeout(resumeAutoScrollTimer.current);
    }

    resumeAutoScrollTimer.current = setTimeout(() => {
      autoScrollPlugin.current.play();
    }, 900);
  }, [carouselApi]);

  const fetchFeaturedItems = async () => {
    try {
      // Use items_public view so all users can see featured items (items table RLS restricts to owners)
      const { data, error } = await supabase
        .from("items_public")
        .select("id, title, price_per_day, category, city, images")
        .eq("is_featured", true)
        .eq("listing_status", "published")
        .gt("featured_until", new Date().toISOString());

      if (error) throw error;
      const real = dedupeById(((data ?? []) as FeaturedItem[]));
      console.log("Featured items found:", real.length);
      // Fall back to mock items so the section is never empty while inventory grows.
      setAllItems(real.length > 0 ? real : MOCK_FEATURED_ITEMS);
    } catch (error) {
      console.error("Error fetching featured items:", error);
      setAllItems(MOCK_FEATURED_ITEMS);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (displayedItems.length === 0) {
    return null;
  }

  const hasMoreItems = allItems.length > DISPLAY_COUNT;

  return (
    <section ref={sectionRef} className="relative py-8 overflow-hidden bg-sky-50">
      <motion.div style={{ y: contentY, opacity }} className="container px-4 sm:px-6 relative">
        <div className="flex flex-col items-center gap-3 mb-6 text-center">
          <div className="p-3 rounded-xl bg-gradient-to-br from-pink-100 via-pink-50 to-purple-100">
            <Sparkles className="h-6 w-6 text-slate-900" />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              Featured Items
            </h2>
            <p className="mt-1 text-black font-semibold">
              Premium listings from our community
            </p>
          </div>
          {/* Trust indicators */}
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5 text-[#030303] font-medium">
              <Shield className="h-4 w-4 text-black" />
              Secure Payments
            </span>
            <span className="flex items-center gap-1.5 text-black font-medium text-left">
              <Star className="h-4 w-4 text-black" />
              Verified Owners
            </span>
          </div>
        </div>

        <Carousel
          opts={{
            align: "start",
            loop: true,
            dragFree: true,
            containScroll: false,
          }}
          plugins={[autoScrollPlugin.current]}
          setApi={setCarouselApi}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {displayedItems.map((item, idx) => (
              <CarouselItem key={item.id} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/4">
                <Link
                  to={item.id.startsWith("mock-") ? categoryHref(item.category) : `/item/${item.id}`}
                  aria-label={item.id.startsWith("mock-") ? `Browse ${item.category} items like ${item.title}` : `View ${item.title}`}
                >
                  <Card className="overflow-hidden hover:shadow-2xl hover:shadow-foreground/10 transition-all duration-300 cursor-pointer group border border-border rounded-xl bg-card relative">
                    {/* Featured badge with soft gradient */}
                    <div className="absolute top-3 left-3 z-10">
                      <Badge className="bg-gradient-to-br from-pink-100 via-pink-50 to-purple-100 text-slate-900 border border-border/40 text-xs font-semibold px-3 py-1.5 shadow-sm hover:from-pink-200 hover:via-pink-100 hover:to-purple-200">
                        <Sparkles className="h-3 w-3 mr-1.5" />
                        Featured
                      </Badge>
                    </div>
                    
                    <div className="relative overflow-hidden h-52 rounded-t-xl">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-[1]" />
                      <img
                        src={item.images[0] || "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=400&h=300&fit=crop"}
                        alt={item.title}
                        loading={idx === 0 ? "eager" : "lazy"}
                        decoding="async"
                        {...(idx === 0 ? { fetchPriority: "high" as const } : {})}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>

                    <CardContent className="p-4 bg-card h-[120px] flex flex-col justify-between">
                      <div>
                        <h3 className="font-semibold text-base mb-1 text-foreground line-clamp-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {item.category} • {item.city}
                        </p>
                      </div>
                      <p className="text-lg font-bold text-foreground">
                        ${item.price_per_day}
                        <span className="text-sm font-normal text-muted-foreground">/day</span>
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              </CarouselItem>
            ))}
          </CarouselContent>
          {displayedItems.length > 4 && (
            <>
              <CarouselPrevious
                aria-label="Show previous featured item"
                onClick={() => moveCarousel("previous")}
                className="flex left-2 sm:-left-4 z-20 h-10 w-10 bg-background border-border shadow-lg hover:bg-muted hover:text-foreground"
              />
              <CarouselNext
                aria-label="Show next featured item"
                onClick={() => moveCarousel("next")}
                className="flex right-2 sm:-right-4 z-20 h-10 w-10 bg-background border-border shadow-lg hover:bg-muted hover:text-foreground"
              />
            </>
          )}
        </Carousel>

        {hasMoreItems && (
          <div className="flex justify-center mt-8">
            <Button
              variant="outline"
              onClick={showNewItems}
              className="gap-2 border-border hover:bg-muted hover:text-foreground transition-all"
            >
              <RefreshCw className="h-4 w-4" />
              See More Featured
            </Button>
          </div>
        )}
      </motion.div>
    </section>
  );
};

export default FeaturedItems;
